import React, { useMemo, useState } from 'react';
import { CheckCircleIcon, Loader2Icon, AlertCircleIcon, ClipboardCheckIcon, SettingsIcon } from 'lucide-react';
import { FieldCell, FieldConfig } from './FieldInput';
import { AdminConfigPanel, FieldConfigWithEnabled } from './AdminConfigPanel';
// Master catalog of all available fields for mortgage applications
const ALL_AVAILABLE_FIELDS: FieldConfig[] = [{
  id: 'fullName',
  label: 'Full Name',
  type: 'text'
}, {
  id: 'dob',
  label: 'Date of Birth',
  type: 'date'
}, {
  id: 'ssn',
  label: 'Social Security Number',
  type: 'text',
  placeholder: 'XXX-XX-XXXX'
}, {
  id: 'email',
  label: 'Email Address',
  type: 'text',
  placeholder: 'name@example.com'
}, {
  id: 'phone',
  label: 'Phone Number',
  type: 'text',
  placeholder: '(555) 000-0000'
}, {
  id: 'income',
  label: 'Annual Income',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'monthlyDebt',
  label: 'Monthly Debt Payments',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'employmentStatus',
  label: 'Employment Status',
  type: 'select',
  options: ['Employed', 'Self-Employed', 'Retired', 'Unemployed']
}, {
  id: 'employerName',
  label: 'Employer Name',
  type: 'text'
}, {
  id: 'yearsEmployed',
  label: 'Years at Current Employer',
  type: 'number',
  placeholder: '0'
}, {
  id: 'loanAmount',
  label: 'Loan Amount Requested',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'downPayment',
  label: 'Down Payment Amount',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'propertyAddress',
  label: 'Property Address',
  type: 'text'
}, {
  id: 'propertyType',
  label: 'Property Type',
  type: 'select',
  options: ['Single Family', 'Condo', 'Townhouse', 'Multi-Family', 'Manufactured']
}, {
  id: 'propertyValue',
  label: 'Estimated Property Value',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'loanType',
  label: 'Loan Type',
  type: 'select',
  options: ['Conventional', 'FHA', 'VA', 'USDA', 'Jumbo']
}, {
  id: 'loanPurpose',
  label: 'Loan Purpose',
  type: 'select',
  options: ['Purchase', 'Refinance', 'Cash-Out Refinance', 'Home Equity']
}, {
  id: 'creditScore',
  label: 'Credit Score Range',
  type: 'select',
  options: ['Excellent (750+)', 'Good (700-749)', 'Fair (650-699)', 'Below 650']
}, {
  id: 'closingDate',
  label: 'Desired Closing Date',
  type: 'date'
}, {
  id: 'coBorrowerName',
  label: 'Co-Borrower Name',
  type: 'text'
}, {
  id: 'coBorrowerIncome',
  label: 'Co-Borrower Annual Income',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'bankruptcyHistory',
  label: 'Bankruptcy History',
  type: 'select',
  options: ['None', 'Within last 2 years', '2-4 years ago', 'Over 4 years ago']
}, {
  id: 'citizenshipStatus',
  label: 'Citizenship Status',
  type: 'select',
  options: ['US Citizen', 'Permanent Resident', 'Non-Permanent Resident', 'Non-Resident']
}, {
  id: 'militaryService',
  label: 'Military Service',
  type: 'select',
  options: ['None', 'Active Duty', 'Veteran', 'Reserves/National Guard', 'Surviving Spouse']
}, {
  id: 'mailingAddress',
  label: 'Mailing Address',
  type: 'text'
}, {
  id: 'currentRent',
  label: 'Current Monthly Rent/Mortgage',
  type: 'number',
  placeholder: '$0'
}];
// Default fields that are initially added to the card
const DEFAULT_FIELD_IDS = ['fullName', 'dob', 'income', 'employmentStatus', 'employerName', 'loanAmount', 'propertyAddress', 'loanType'];
const INITIAL_FIELDS: FieldConfigWithEnabled[] = DEFAULT_FIELD_IDS.map(id => {
  const field = ALL_AVAILABLE_FIELDS.find(f => f.id === id)!;
  return {
    ...field,
    enabled: true
  };
});
export function RequiredFieldsCard() {
  const [fields, setFields] = useState<FieldConfigWithEnabled[]>(INITIAL_FIELDS);
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  // Compute which fields from the catalog haven't been added yet
  const availableFields = useMemo(() => {
    const addedIds = new Set(fields.map(f => f.id));
    return ALL_AVAILABLE_FIELDS.filter(f => !addedIds.has(f.id));
  }, [fields]);
  // Calculate progress based on enabled fields
  const enabledFields = fields.filter(f => f.enabled);
  const totalCount = enabledFields.length;
  const completedCount = enabledFields.filter(f => (savedValues[f.id] || '').trim() !== '').length;
  const remainingCount = totalCount - completedCount;
  const isAllComplete = remainingCount === 0 && totalCount > 0;
  // Filter to only show incomplete fields (not yet saved with a value) AND enabled
  const visibleFields = useMemo(() => {
    return fields.filter(field => {
      if (!field.enabled) return false;
      const saved = savedValues[field.id] || '';
      return saved.trim() === '';
    });
  }, [fields, savedValues]);
  // Check if there are unsaved changes
  const isDirty = useMemo(() => {
    return Object.entries(pendingEdits).some(([key, val]) => {
      const savedVal = savedValues[key] || '';
      return val.trim() !== savedVal.trim();
    });
  }, [pendingEdits, savedValues]);
  const handleFieldChange = (id: string, value: string) => {
    setPendingEdits(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setSavedValues(prev => {
        const newSaved = {
          ...prev
        };
        Object.entries(pendingEdits).forEach(([key, val]) => {
          newSaved[key] = val;
        });
        return newSaved;
      });
      setPendingEdits({});
      setIsSaving(false);
    }, 600);
  };
  const handleCancel = () => {
    setPendingEdits({});
  };
  // Admin Config Handlers
  const handleToggleField = (id: string) => {
    setFields(prev => prev.map(f => f.id === id ? {
      ...f,
      enabled: !f.enabled
    } : f));
  };
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    setFields(prev => {
      const newFields = [...prev];
      const temp = newFields[index - 1];
      newFields[index - 1] = newFields[index];
      newFields[index] = temp;
      return newFields;
    });
  };
  const handleMoveDown = (index: number) => {
    if (index === fields.length - 1) return;
    setFields(prev => {
      const newFields = [...prev];
      const temp = newFields[index + 1];
      newFields[index + 1] = newFields[index];
      newFields[index] = temp;
      return newFields;
    });
  };
  const handleAddField = (field: FieldConfig) => {
    setFields(prev => [...prev, {
      ...field,
      enabled: true
    }]);
  };
  const handleRemoveField = (id: string) => {
    setFields(prev => prev.filter(f => f.id !== id));
    setSavedValues(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    setPendingEdits(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
  };
  return <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200 overflow-hidden max-w-4xl mx-auto flex flex-col relative" data-id="element-63">
      {/* Top Accent Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-600 to-indigo-500" data-id="element-64" />

      {/* Content wrapper with top padding to clear accent bar */}
      <div className="pt-1" data-id="element-65">
        {/* Save/Cancel Toolbar - only when NOT in config mode */}
        {!isConfigOpen && isDirty && <div className="px-6 py-3 bg-indigo-50/90 backdrop-blur-sm border-b border-indigo-100 flex items-center justify-between animate-in slide-in-from-top-2 fade-in duration-200" data-id="element-66">
            <div className="flex items-center gap-2.5 text-sm text-indigo-900 font-medium" data-id="element-67">
              <AlertCircleIcon className="w-4 h-4 text-indigo-600" data-id="element-68" />
              <span data-id="element-69">You have unsaved changes</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-70">
              <button onClick={handleCancel} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-50" data-id="element-71">
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center justify-center px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm shadow-indigo-300/50 transition-all disabled:opacity-70 min-w-[100px]" data-id="element-72">
                {isSaving ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-73" /> : 'Save Changes'}
              </button>
            </div>
          </div>}

        {/* Header - always visible */}
        <div className="px-6 py-5 border-b border-slate-100 bg-white flex items-center justify-between gap-4" data-id="element-74">
          <div data-id="element-75">
            <h2 className="text-lg font-semibold text-slate-900 tracking-tight" data-id="element-76">
              {isConfigOpen ? 'Admin Configuration' : 'Required Information'}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-77">
              {isConfigOpen ? 'Add, remove, toggle, and reorder fields' : totalCount === 0 ? 'No fields configured' : remainingCount === 0 ? 'All fields complete' : `Please complete the remaining ${remainingCount} field${remainingCount !== 1 ? 's' : ''}`}
            </p>
          </div>

          <button onClick={() => setIsConfigOpen(!isConfigOpen)} className={`p-2 rounded-lg transition-colors ${isConfigOpen ? 'bg-indigo-100 text-indigo-700' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'}`} aria-label="Configure fields" data-id="element-78">
            <SettingsIcon className="w-5 h-5" data-id="element-79" />
          </button>
        </div>

        {/* Admin Config Panel - replaces all content below header */}
        {isConfigOpen ? <AdminConfigPanel fields={fields} availableFields={availableFields} onToggleField={handleToggleField} onMoveUp={handleMoveUp} onMoveDown={handleMoveDown} onAddField={handleAddField} onRemoveField={handleRemoveField} onClose={() => setIsConfigOpen(false)} data-id="element-80" /> : <>
            {/* Completion Banner */}
            {isAllComplete && !isDirty && totalCount > 0 && <div className="px-6 py-5 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100 flex items-start sm:items-center gap-4" data-id="element-81">
                <div className="bg-emerald-100 p-2.5 rounded-full flex-shrink-0" data-id="element-82">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-83" />
                </div>
                <div data-id="element-84">
                  <h3 className="text-base font-semibold text-emerald-900" data-id="element-85">
                    Application Ready
                  </h3>
                  <p className="text-sm text-emerald-700 mt-0.5" data-id="element-86">
                    All required fields are complete. Your application is ready
                    for review by your loan officer.
                  </p>
                </div>
              </div>}

            {/* Two-Column Grid or Empty State */}
            {visibleFields.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 bg-white" data-id="element-87">
                {visibleFields.map((field, index) => {
            const currentValue = pendingEdits[field.id] ?? savedValues[field.id] ?? '';
            const savedValue = savedValues[field.id] ?? '';
            const isLeftColumn = index % 2 === 0;
            const totalRows = Math.ceil(visibleFields.length / 2);
            const currentRow = Math.floor(index / 2);
            const isLastRow = currentRow === totalRows - 1;
            const isLastItem = index === visibleFields.length - 1;
            return <FieldCell key={field.id} field={field} value={currentValue} savedValue={savedValue} onChange={val => handleFieldChange(field.id, val)} isLastInRow={!isLeftColumn || isLastItem && visibleFields.length % 2 === 1} isLastRow={isLastRow} data-id="element-88" />;
          })}
              </div> : <div className="px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-89">
                <div className="bg-slate-100 p-4 rounded-full mb-4" data-id="element-90">
                  <ClipboardCheckIcon className="w-7 h-7 text-slate-400" data-id="element-91" />
                </div>
                <p className="text-base font-semibold text-slate-700" data-id="element-92">
                  {totalCount === 0 ? 'No Fields Configured' : 'No Required Fields Missing'}
                </p>
                <p className="text-sm text-slate-500 mt-1" data-id="element-93">
                  {totalCount === 0 ? 'Use the gear icon above to configure required fields.' : 'All required information has been provided.'}
                </p>
              </div>}
          </>}
      </div>
    </div>;
}